import { createFileRoute } from '@tanstack/react-router';
import { UserProfile, useUser, useClerk } from '@clerk/clerk-react';
import { useState } from 'react';

export const Route = createFileRoute('/app/account')({
  component: AccountPage,
});

function AccountPage() {
  const { user } = useUser();
  const clerk = useClerk();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailConfirmation, setEmailConfirmation] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const userEmail = user?.primaryEmailAddress?.emailAddress || '';

  const handleDeleteAccount = async () => {
    setDeleteError('');

    // Validate email confirmation
    if (emailConfirmation !== userEmail) {
      setDeleteError('Email does not match. Please type your email address exactly as shown.');
      return;
    }

    setIsDeleting(true);
    try {
      // Ensure we have an active session
      const session = clerk.session;

      if (!session) {
        throw new Error('No active session found. Please refresh the page and try again.');
      }

      // Get a fresh session token to ensure authentication is current
      // This prevents Clerk's "additional verification" error for logged-in users
      await session.getToken({ skipCache: true });

      // Delete the user account with fresh authentication
      await user?.delete();

      // Success - user will be automatically signed out and redirected
      // The user.deleted webhook will trigger Convex cascade deletion
      // Redirect will happen automatically via Clerk
    } catch (error) {
      setIsDeleting(false);

      // Never show verification errors to the user - they're confusing
      // Instead, provide clear actionable guidance
      const errorMessage = error instanceof Error ? error.message : '';

      if (errorMessage.includes('verification') || errorMessage.includes('Unauthorized')) {
        // Authentication issue - guide user to refresh and retry
        setDeleteError(
          'Your session has expired. Please refresh this page, then try deleting your account again.'
        );
      } else if (errorMessage.includes('session')) {
        // Session-related error
        setDeleteError(
          'Session error. Please refresh the page and try again.'
        );
      } else if (errorMessage) {
        // Other specific errors
        setDeleteError(
          `Account deletion failed: ${errorMessage}. Please contact support if this persists.`
        );
      } else {
        // Generic fallback
        setDeleteError(
          'Account deletion failed. Please refresh the page and try again, or contact support.'
        );
      }
    }
  };

  return (
    <div className="min-h-screen bg-terminal-black p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-terminal-orange mb-6 terminal-glow">
          [ACCOUNT SETTINGS]
        </h1>
        <div className="terminal-box">
          <UserProfile
            appearance={{
              elements: {
                rootBox: 'bg-terminal-black',
                card: 'bg-terminal-black border-terminal-orange',
                headerTitle: 'text-terminal-orange',
                headerSubtitle: 'text-terminal-orange-dim',
                socialButtonsBlockButton: 'terminal-button',
                formButtonPrimary: 'terminal-button',
                formFieldInput: 'terminal-input',
                navbarButton: 'text-terminal-orange hover:text-terminal-orange-bright',
                profileSectionTitle: 'text-terminal-orange',
                profileSectionContent: 'text-terminal-orange-dim',
              },
              variables: {
                colorPrimary: '#ff8800',
                colorBackground: '#000000',
                colorText: '#ff8800',
                colorInputBackground: '#000000',
                colorInputText: '#ff8800',
              },
            }}
          />
        </div>

        {/* Danger Zone */}
        <div className="terminal-box mt-8 border-red-500">
          <h2 className="text-xl font-bold text-red-500 mb-4 terminal-glow">
            [⚠️ DANGER ZONE]
          </h2>
          <div className="bg-black bg-opacity-50 p-6 rounded border border-red-500">
            <h3 className="text-lg font-semibold text-red-400 mb-2">Delete Account</h3>
            <p className="text-terminal-orange-dim mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <ul className="list-disc list-inside text-terminal-orange-dim mb-4 space-y-1">
              <li>All API keys will be revoked</li>
              <li>All credits will be forfeited</li>
              <li>All usage history will be deleted</li>
              <li>All notifications will be removed</li>
            </ul>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded border-2 border-red-500 transition-colors terminal-glow"
            >
              Delete My Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="terminal-box border-red-500 max-w-lg w-full bg-terminal-black">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-red-500 mb-4 terminal-glow">
                [⚠️ CONFIRM ACCOUNT DELETION]
              </h2>

              <div className="mb-6">
                <p className="text-terminal-orange mb-4">
                  This action is <span className="text-red-500 font-bold">PERMANENT</span> and cannot be undone.
                </p>
                <p className="text-terminal-orange-dim mb-4">
                  To confirm, please type your email address:
                </p>
                <p className="text-cyan-400 font-mono mb-4 bg-black bg-opacity-50 p-2 rounded border border-cyan-400">
                  {userEmail}
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-terminal-orange mb-2 font-semibold">
                  Email Confirmation:
                </label>
                <input
                  type="email"
                  value={emailConfirmation}
                  onChange={(e) => {
                    setEmailConfirmation(e.target.value);
                    setDeleteError('');
                  }}
                  placeholder="Type your email address"
                  className="w-full bg-black border-2 border-cyan-400 text-cyan-400 p-3 rounded font-mono focus:outline-none focus:border-cyan-300"
                  disabled={isDeleting}
                />
              </div>

              {deleteError && (
                <div className="mb-4 p-3 bg-red-900 bg-opacity-30 border border-red-500 rounded">
                  <p className="text-red-400">{deleteError}</p>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || emailConfirmation !== userEmail}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded border-2 border-red-500 transition-colors terminal-glow"
                >
                  {isDeleting ? 'Deleting Account...' : 'Yes, Delete My Account'}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setEmailConfirmation('');
                    setDeleteError('');
                  }}
                  disabled={isDeleting}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-terminal-orange font-bold py-3 px-6 rounded border-2 border-terminal-orange transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}